import { Row, RowItemType, Section, SectionType, SelectorIdentifier, UIElementType } from '../../models';
import { calculateMaintenanceInterval, convertDate, createSmartTranslationMessage } from '../../models/utils';
import { LocationCommunicationType, MonitorSeriesNames } from '../../../location-card.constants';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { LocationCardMonitorSeries } from 'app/shared/models/monitorsetting';
import {
    enableField,
    loadInstallationTypes,
    setGlobalConfig,
    pickMapCoords,
    openModemProviderDialog,
    statusChange,
    tagKeyChange,
    disableField,
} from '../../actions';
import { BasicInfoCommonFieldIds } from '../common.models';
import { RainInfoFieldIds, RainSectionIds } from './base-config-models';
import { DEFAULT_APN_PROVIDER } from 'app/shared/constant';
import { ValidatorIdentifier } from '../../models/validator-identifier';
import { field, FieldActions } from '../../core/field-schema';
import { required, maxLength, pattern, custom } from '../../core/validator-builders';
import { LocationCardStatus, RainGaugeLocation, SampleRateElement, ScanRateElement } from '../../../models/location-card.model';
import { Selectable } from 'app/shared/models/selectable';
import { RainAlertLocationCardState, RainAlertMonitorConfiguration } from './models';
import { TextField } from '../../models/field-models';
import { LocationStatus, RainLocation } from 'app/shared/models/locations';

export const customPropertyRowGenerator = (index: number, populateValues?: { key: string; value: string }): Row => {
    return {
        info: {
            id: `custom-property-${index}`,
            panelClass: 'custom-50p',
        },
        items: [
            field.autocomplete<string, string>(`${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`, 'key', {
                optionsSelector: SelectorIdentifier.TagKeyAutocomplete,
                value: populateValues?.key,
                placeholder: 'ADD_EDIT.INPUT_TAG_NAME',
                validators: [required(), maxLength({ value: 128 })],
                panelClass: 'width-90',
                clearable: false,
                autoClearOnFocus: false,
                displayMapper: (option) => option,
                valueMapper: (option) => option,
                events: {
                    onBlur: (value) => [
                        tagKeyChange({
                            fieldId: `${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`,
                            newValue: value,
                            isMp1: true,
                        }),
                    ],
                },
            }),

            field.text(`${BasicInfoCommonFieldIds.CustomPropertyValue}-${index}`, 'value', {
                value: populateValues?.value,
                placeholder: 'ADD_EDIT.INPUT_TAG_VALUE',
                validators: [required(), maxLength({ value: 128 })],
                panelClass: 'width-90',
            }),
        ],
    };
};

/**
 * Rain form configuration
 */
export const basicInfo = (state: RainAlertLocationCardState): Section => {
    const payload = state.locationDetails && state.locationDetails.length > 0 ? state.locationDetails[0] : null;
    const monitorConfig = state.monitorConfiguration as RainAlertMonitorConfiguration;
    const settings = state.settings;

    const rainGuage =
        payload && payload.assignedRainGaugeLocationId
            ? state.options.rainGauges.data.find((v) => v.locationId === payload.assignedRainGaugeLocationId)
            : null;

    const isEditMode = !!payload;
    const isSwitchMonitor = payload && payload.series !== MonitorSeriesNames.rainAlert;
    const showCoordsButton = state.locationCardData.enableSelectOnMap;

    return {
        info: {
            id: RainSectionIds.BasicInfo,
            title: 'ADD_EDIT.ADD_EDIT_INFORMATION',
            displayTitle: true,
            expanded: true,
            expandable: true,
            panelClass: 'basic-info-section',
        },
        rows: [
            // Row 1: Location Name and Series
            {
                info: {
                    id: 'basic-info-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.LocationName, 'locationName', {
                        value: isEditMode ? payload.locationName : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_NAME',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            custom({
                                identifier: ValidatorIdentifier.UniqueLocationName,
                                message: 'COMMON.LOCATION_NAME_EXISTS',
                            }),
                            maxLength({
                                value: 63,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.trim().charAt(0).toUpperCase() + value.trim().slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    field.select<LocationCardMonitorSeries, string>(BasicInfoCommonFieldIds.Series, 'series', {
                        panelClass: 'custom-width',
                        value: MonitorSeriesNames.rainAlert,
                        placeholder: 'COMMON.MONITOR_SERIES_PLACEHOLDER',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.MonitorSeriesOptions,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: (value) => [
                                enableField({ fieldId: BasicInfoCommonFieldIds.InstallationType }),
                                loadInstallationTypes({ installationId: value.monitorSeriesID }),
                                setGlobalConfig({ monitorSeriesName: value.value }),
                            ],
                        },
                    }),
                ],
            },

            // Row 2: Serial Number and Status
            {
                info: {
                    id: 'basic-info-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.SerialNumber, 'serialnumber', {
                        panelClass: 'custom-width',
                        value: isEditMode ? payload.serialNumber : null,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SERIAL',
                        validators: [
                            required(),
                            maxLength({ value: 5 }),
                            pattern({
                                value: REGEX_CONFIG.numericOnlyPattern,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.SERIAL_NUMBER_ERROR',
                            }),
                        ],
                        events: {
                            onInput: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.replace(/[^0-9]/g, '');
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    {
                        id: BasicInfoCommonFieldIds.StatusSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'status-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'status-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'status-row',
                                        panelClass: 'status-inner-row flex align-items-center gap-4',
                                    },
                                    items: [
                                        field.select<LocationCardStatus, number>(
                                            BasicInfoCommonFieldIds.Status,
                                            'status',
                                            {
                                                value: isEditMode ? payload.status : 0,
                                                panelClass: 'custom-width',
                                                disabled: !isEditMode,
                                                label: 'Status',
                                                valueMapper: (option) => option.key,
                                                displayMapper: (option) => option.name,
                                                validators: [required()],
                                                optionsSelector: SelectorIdentifier.LocationStatuses,
                                                events: {
                                                    onChange: () => [statusChange({ isMp1: true })],
                                                },
                                            },
                                        ),
                                        field.select<number, number>(
                                            BasicInfoCommonFieldIds.MaintenanceInterval,
                                            'maintenanceInterval',
                                            {
                                                value: isEditMode
                                                    ? calculateMaintenanceInterval(payload.maintenanceEnd)
                                                    : 2,
                                                label: 'CUSTOMER_EDITOR.HOURS',
                                                valueMapper: (option) => option,
                                                displayMapper: (option) => String(option),
                                                panelClass: 'width-15',
                                                validators: [required()],
                                                visible: isEditMode
                                                    ? payload.status === LocationStatus.Maintenance
                                                    : false,
                                                optionsSelector: SelectorIdentifier.MaintenanceIntervals,
                                            },
                                        ),
                                        {
                                            id: BasicInfoCommonFieldIds.MaintenanceEnd,
                                            name: 'maintenanceEnd',
                                            panelClass: 'width-25 field-status-info',
                                            label: isEditMode
                                                ? createSmartTranslationMessage('LOCATION_CARD_DYNAMIC_TRANSLATION.RETURN_TO_ACTIVE', {
                                                    date: convertDate(payload.maintenanceEnd, state.customer?.dateFormat, state.customer?.timeFormat)
                                                  })
                                                : 'CUSTOMER_EDITOR.HOURS',
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Text,
                                            visible: isEditMode ? payload.status === LocationStatus.Maintenance : false,
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },

            // Row 3: Communication and IP Address
            {
                info: {
                    id: 'basic-info-row-4',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<LocationCardMonitorSeries, string>(
                        BasicInfoCommonFieldIds.CommunicationType,
                        'communicationType',
                        {
                            value: isEditMode
                                ? String(payload.communicationType)
                                : String(LocationCommunicationType.Wireless),
                            placeholder: 'COMMON.COMMUNICATION_TYPE_PLACEHOLDER',
                            validators: [required()],
                            panelClass: 'custom-width',
                            optionsSelector: SelectorIdentifier.CommunicationTypes,
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            events: {
                                onChange: (option) => {
                                    const isWireless = Number(option.value) === LocationCommunicationType.Wireless;
                                    return [
                                        isWireless
                                            ? enableField({ fieldId: BasicInfoCommonFieldIds.IpAddress })
                                            : disableField({ fieldId: BasicInfoCommonFieldIds.IpAddress }),
                                    ];
                                },
                            },
                        },
                    ),
                    field.text(BasicInfoCommonFieldIds.IpAddress, 'ipaddress', {
                        value: isEditMode ? payload.connectionString : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_IP',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.ipaddressPattern,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.IP_ADDRESS_ERROR',
                            }),
                            maxLength({ value: 100 }),
                        ],
                        disabled: isEditMode ? payload.communicationType !== LocationCommunicationType.Wireless : false,
                    }),
                ],
            },

            // Row 4: APN Provider and Description
            {
                info: {
                    id: 'basic-info-row-5',
                    panelClass: 'custom-50p',
                },
                items: [
                    {
                        id: 'modem-provider-section',
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'modem-provider-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'modem-provider-row',
                                        panelClass: 'flex custom-flat-button',
                                    },
                                    items: [
                                        field.select<Selectable, string>(
                                            BasicInfoCommonFieldIds.ApnProvider,
                                            'apnProvider',
                                            {
                                                value: isEditMode ? monitorConfig?.apn ?? DEFAULT_APN_PROVIDER : DEFAULT_APN_PROVIDER,
                                                valueMapper: (option) => option.name,
                                                displayMapper: (option) => option.name,
                                                panelClass: 'full-width',
                                                placeholder: 'COMMON.MODEM_PROVIDER',
                                                validators: [required()],
                                                optionsSelector: SelectorIdentifier.ModemProviders,
                                            },
                                        ),
                                        {
                                            id: BasicInfoCommonFieldIds.ApnProviderBtn,
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Button,
                                            name: 'modemProviderBtn',
                                            iconName: 'edit',
                                            tooltip: 'COMMON.EDIT_TOOLTIP',
                                            panelClass: 'no-margin',
                                            events: {
                                                onClick: () => [openModemProviderDialog()],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                    field.text(BasicInfoCommonFieldIds.Description, 'description', {
                        value: isEditMode ? payload.description : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LOCATION',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            maxLength({
                                value: 257,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                ],
            },

            // Row 5: Rain Gauge (only one field, no Manhole Depth for Rain Alert)
            {
                info: {
                    id: 'location-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.autocomplete<RainLocation, string>(
                        BasicInfoCommonFieldIds.AssignedRainGauge,
                        'assignedRainGauge',
                        {
                            value: rainGuage ? rainGuage.locationName : null,
                            disabled: true,
                            panelClass: 'custom-width',
                            label: 'Assigned Rain Gauge',
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ASSIGNED',
                            clearable: true,
                            optionsSelector: SelectorIdentifier.RainGauges,
                            displayMapper: (option) => option.locationName,
                            valueMapper: (option) => option.locationName,
                            validators: [
                                maxLength({
                                    value: 50,
                                }),
                            ],
                        },
                    ),
                ],
            },

            // Row 6: Manhole Address and Coordinates
            {
                info: {
                    id: 'location-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.text(BasicInfoCommonFieldIds.ManholeAddress, 'manholeaddress', {
                        value: isEditMode ? payload.manholeAddress : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MANHOLE_ADDRESS',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.alphaNumericWitSpecialSymbols_Pattern,
                                message: 'COMMON.LOCATION_NAME_DESCRIPTION_MANHOLE_ADDRESS_ERROR',
                            }),
                            maxLength({
                                value: 64,
                            }),
                        ],
                        events: {
                            onChange: function (this: TextField & { _actions: FieldActions<string> }, value) {
                                const transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                return [this._actions.setValue(transformedValue)];
                            },
                        },
                    }),
                    {
                        id: BasicInfoCommonFieldIds.CoordinatesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CoordinatesSection,
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'coordinates-group custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'coordinates-row',
                                        panelClass: `coordinates-inner-row ${
                                            showCoordsButton ? 'flex-end' : 'justify-space-between'
                                        }`,
                                    },
                                    items: [
                                        field.number(BasicInfoCommonFieldIds.Latitude, 'latitude', {
                                            value: isEditMode ? payload.latitude : 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LATITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.latitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        field.number(BasicInfoCommonFieldIds.Longitude, 'longitude', {
                                            value: isEditMode ? payload.longitude : 0,
                                            panelClass: 'custom-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_LONGITUDE',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.longitudePattern,
                                                    message: 'COMMON.LONGITUDE_ERROR',
                                                }),
                                            ],
                                        }),
                                        {
                                            id: 'move-location-icon',
                                            name: 'moveLocationIcon',
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Image,
                                            visible: !!showCoordsButton,
                                            imgSrc: '/assets/images/icons/move_icon.png',
                                            imgHeight: 18,
                                            imgWidth: 18,
                                            panelClass: 'move-icon',
                                            events: {
                                                onClick: () => [pickMapCoords()],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                ],
            },

            // Row 7: Sample Rates
            {
                info: {
                    id: 'settings-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<SampleRateElement, number>(BasicInfoCommonFieldIds.SampleRate, 'samplerate', {
                        value: isEditMode ? monitorConfig?.samplerate ?? null : null,
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SAMPLE_RATE',
                        validators: [required(), custom({ identifier: ValidatorIdentifier.NormalRateValidator })],
                        optionsSelector: SelectorIdentifier.SampleRates,
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: (option) => [
                                Number(option.value) === 120
                                    ? enableField({ fieldId: RainInfoFieldIds.LogUkIntensity })
                                    : disableField({ fieldId: RainInfoFieldIds.LogUkIntensity }),
                            ],
                        },
                    })
                ],
            },

            // Row 8: Time Settings
            {
                info: {
                    id: 'settings-row-2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(BasicInfoCommonFieldIds.SyncTime, 'synctime', {
                        value: isEditMode ? monitorConfig?.synctime ?? true : true,
                        panelClass: 'custom-width item-flex-center',
                        label: 'ADD_EDIT.CLOCK_SYNCHRONIZATON',
                    }),
                    field.toggle(BasicInfoCommonFieldIds.UseDaylightSaving, 'usedaylightsaving', {
                        value: isEditMode ? monitorConfig?.usedaylightsaving ?? true : true,
                        panelClass: 'custom-width item-flex-center',
                        label: 'ADD_EDIT.DAYLIGHT_SAVING',
                    }),
                ],
            },

            // Row 9: Installation Date - No Installation Type for Rain Alert
            {
                info: {
                    id: 'settings-row-3',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.date(BasicInfoCommonFieldIds.InstallationDate, 'installationDate', {
                        label: 'Installation Date',
                        placeholder: 'Select date',
                        dateFormat: settings.dateFormat,
                        datePickerType: 'date',
                        timeInterval: 1,
                        panelClass: 'full-width',
                        value: isEditMode ? payload.installationDate : new Date(),
                    }),
                ],
            },

            // Custom properties section
            {
                info: {
                    id: 'custom-properties-container-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: BasicInfoCommonFieldIds.CustomPropertiesSection,
                                title: 'ADD_EDIT.TAGS_SECTION_NAME',
                                displayTitle: false,
                                displayActionBtns: true,
                                type: SectionType.Array,
                                expanded: false,
                                expandable: false,
                                itemGenerator: customPropertyRowGenerator,
                                panelClass: 'custom-properties-panel',
                                maxItems: 64,
                            },
                            rows:
                                isEditMode && payload.customProperties
                                    ? payload.customProperties.map((item, index) =>
                                          customPropertyRowGenerator(index, item),
                                      )
                                    : [],
                        },
                    },
                ],
            },
        ],
    };
};
